import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import InsightPanel from '../Insight/InsightPanel';
import { useInsightStore } from '../../stores/useInsightStore';
import PageLoadBoundary from './PageLoadBoundary';

const SIDEBAR_WIDTH = 240;
const INSIGHT_WIDTH = 320;
const TOPBAR_HEIGHT = 56;

/**
 * 桌面三栏布局组件。
 * - 左侧 Sidebar（240px，可折叠）
 * - 中间主内容区（flex:1）
 * - 右侧 InsightPanel（320px，默认隐藏）
 */
const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const location = useLocation();
  const { isOpen: insightOpen, closePanel } = useInsightStore();
  const showInsight = insightOpen && location.pathname === '/chat';
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        width={SIDEBAR_WIDTH}
      />

      {/* 主内容区域 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          transition: 'margin-left 0.3s ease',
        }}
      >
        <TopBar
          height={TOPBAR_HEIGHT}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
        />

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {/* 路由页面内容 */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              bgcolor: 'background.default',
            }}
          >
            <PageLoadBoundary key={location.pathname}>
              <Outlet />
            </PageLoadBoundary>
          </Box>

          {/* Insight Panel */}
          <Box
            sx={{
              width: showInsight && !isMobile ? INSIGHT_WIDTH : 0,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.3s ease',
              bgcolor: 'background.paper',
              borderLeft: showInsight && !isMobile ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {showInsight && !isMobile && (
              <InsightPanel onClose={closePanel} />
            )}
          </Box>
        </Box>
      </Box>
      {isMobile && <Drawer anchor="right" open={showInsight} onClose={closePanel} sx={{ '& .MuiDrawer-paper': { width: 'min(90vw, 360px)' } }}>
        <InsightPanel onClose={closePanel} />
      </Drawer>}
    </Box>
  );
};

export default MainLayout;
